import { OPERATION_IDS, ROUTE_IDS } from '../experience/routes/ids.js';
import { getCanonicalFieldHelpTextKey } from '../experience/descriptors/form-field-annotations.js';
import { SETTINGS_PROFILE_UPDATE, SETTINGS_PREFERENCES_UPDATE, } from '../operations/operation-ids.js';
const tk = (value) => value;
const mk = (value) => value;
const fk = (value) => value;
const ti = (value) => value;
const fdi = (value) => value;
const ffi = (value) => value;
const fsi = (value) => value;
const cdi = (value) => value;
const icon = (value) => value;
const vri = (value) => value;
const SETTINGS_MODULE = mk('settings');
const PROFILE_UPDATE_OPERATION = SETTINGS_PROFILE_UPDATE;
const PREFERENCES_UPDATE_OPERATION = SETTINGS_PREFERENCES_UPDATE;
function field(id, name, overrides) {
    const fieldType = overrides.fieldType ?? 'text';
    return {
        ...overrides,
        id: ffi(id),
        name,
        labelKey: overrides.labelKey ?? tk(id),
        fieldType,
        helpTextKey: overrides.helpTextKey ?? getCanonicalFieldHelpTextKey(id, name, fieldType),
    };
}
function action(input) {
    const kind = input.kind ?? 'command';
    return {
        id: input.id,
        schemaVersion: 1,
        module: SETTINGS_MODULE,
        feature: fk('account-settings'),
        telemetryId: ti(`settings.${input.id}`),
        accessibilityLabelKey: tk(input.labelKey),
        privacyClassification: 'CONFIDENTIAL',
        availability: 'AVAILABLE',
        owner: 'contracts',
        operationId: input.operationId,
        labelKey: tk(input.labelKey),
        iconKey: icon(input.iconKey),
        kind,
        commandId: input.commandId ? cdi(input.commandId) : undefined,
        submitBehavior: kind === 'command' ? 'form_submit' : 'standalone',
        intent: input.intent ?? 'primary',
        idempotencyRequired: false,
        successBehavior: 'NO_OP',
        errorBehavior: 'SHOW_INLINE_ERROR',
        submitConfig: input.submitConfig,
    };
}
export const ACCOUNT_PROFILE_FORM = {
    id: fdi('form.settings.profile'),
    schemaVersion: 1,
    module: SETTINGS_MODULE,
    feature: fk('profile'),
    telemetryId: ti('form.settings.profile'),
    accessibilityLabelKey: tk('settings:profile.title'),
    privacyClassification: 'CONFIDENTIAL',
    availability: 'AVAILABLE',
    owner: 'contracts',
    operationId: PROFILE_UPDATE_OPERATION,
    titleKey: tk('settings:profile.title'),
    descriptionKey: tk('settings:profile.description'),
    mode: 'edit',
    fields: [
        field('field.profile.firstName', 'firstName', {
            labelKey: tk('settings:profile.firstName'),
            layout: { md: 6 },
            validation: { rules: [{ ruleId: vri('required'), messageKey: tk('settings:profile.firstNameRequired') }] },
        }),
        field('field.profile.lastName', 'lastName', {
            labelKey: tk('settings:profile.lastName'),
            layout: { md: 6 },
            validation: { rules: [{ ruleId: vri('required'), messageKey: tk('settings:profile.lastNameRequired') }] },
        }),
        field('field.profile.email', 'email', {
            labelKey: tk('settings:profile.email'),
            layout: { md: 6 },
            state: { readonlyCondition: { operator: 'exists', field: 'field.profile.email' } },
            value: { submitBehavior: 'server-owned' },
        }),
        field('field.profile.phone', 'phone', {
            labelKey: tk('settings:profile.phone'),
            layout: { md: 6 },
            validation: {
                rules: [{
                        ruleId: vri('pattern'),
                        params: { pattern: '^[\\d\\s\\-+()]*$' },
                        messageKey: tk('settings:profile.phoneInvalid'),
                    }],
            },
        }),
        field('field.profile.status', 'status', {
            labelKey: tk('settings:profile.status'),
            layout: { md: 6 },
            state: { readonlyCondition: { operator: 'exists', field: 'field.profile.status' } },
            value: { submitBehavior: 'server-owned' },
        }),
    ],
    sections: [{
            id: fsi('section.profile.identity'),
            titleKey: tk('section.profile.identity'),
            fields: [
                ffi('field.profile.firstName'),
                ffi('field.profile.lastName'),
                ffi('field.profile.email'),
                ffi('field.profile.status'),
                ffi('field.profile.phone'),
            ],
            layout: { columns: 2, gap: 16 },
        }],
    actions: [
        action({ id: 'action.cancel', labelKey: 'common:cancel', iconKey: 'cancel', kind: 'localUi', intent: 'secondary', submitConfig: { validatesForm: false, requiresDirty: true } }),
        action({
            id: 'action.save',
            labelKey: 'common:save',
            iconKey: 'save',
            commandId: 'profile:update',
            operationId: PROFILE_UPDATE_OPERATION,
            submitConfig: { validatesForm: true, requiresDirty: true, resetBehavior: 'toSubmittedValues' },
        }),
    ],
    submission: {
        defaultCommandId: cdi('profile:update'),
        successMessageKey: tk('settings:profile.saved'),
        errorMessageKey: tk('settings:profile.saveError'),
    },
};
/** Only preferences with a live product consumer are exposed here. */
export const ACCOUNT_PREFERENCES_FORM = {
    id: fdi('form.settings.preferences'),
    schemaVersion: 1,
    module: SETTINGS_MODULE,
    feature: fk('preferences'),
    telemetryId: ti('form.settings.preferences'),
    accessibilityLabelKey: tk('settings:preferences.title'),
    privacyClassification: 'INTERNAL',
    availability: 'AVAILABLE',
    owner: 'contracts',
    operationId: PREFERENCES_UPDATE_OPERATION,
    titleKey: tk('settings:preferences.title'),
    descriptionKey: tk('settings:preferences.description'),
    mode: 'edit',
    fields: [
        field('field.preferences.theme', 'theme', {
            labelKey: tk('settings:preferences.theme.label'), fieldType: 'select', layout: { md: 6 },
            options: [
                { value: 'light', labelKey: tk('settings:preferences.themeLight') },
                { value: 'dark', labelKey: tk('settings:preferences.themeDark') },
                { value: 'system', labelKey: tk('settings:preferences.themeAuto') },
            ],
        }),
        field('field.preferences.language', 'language', {
            labelKey: tk('settings:preferences.language.label'), fieldType: 'select', layout: { md: 6 },
            options: [
                { value: 'en', labelKey: tk('settings:preferences.languageEn') },
                { value: 'ne', labelKey: tk('settings:preferences.languageNe') },
            ],
        }),
        field('field.preferences.defaultContext', 'defaultContext', {
            labelKey: tk('settings:preferences.defaultContext.label'), fieldType: 'select', layout: { md: 6 },
        }),
        field('field.preferences.dateFormat', 'dateFormat', {
            labelKey: tk('settings:preferences.dateFormat.label'), fieldType: 'select', layout: { md: 6 },
            options: [
                { value: 'AD', labelKey: tk('settings:preferences.dateFormatAD') },
                { value: 'BS', labelKey: tk('settings:preferences.dateFormatBS') },
                { value: 'DUAL', labelKey: tk('settings:preferences.dateFormatDual') },
            ],
        }),
        field('field.preferences.numberFormat', 'numberFormat', {
            labelKey: tk('settings:preferences.numberFormat.label'), fieldType: 'select', layout: { md: 6 },
            options: [
                { value: 'en-NP', labelKey: tk('settings:preferences.numberFormatEnNP') },
                { value: 'en-US', labelKey: tk('settings:preferences.numberFormatEnUS') },
                { value: 'en-IN', labelKey: tk('settings:preferences.numberFormatEnIN') },
            ],
        }),
        field('field.preferences.timeZone', 'timeZone', {
            labelKey: tk('settings:preferences.timeZone.label'), fieldType: 'select', layout: { md: 6 },
            options: [
                { value: 'Asia/Kathmandu', labelKey: tk('settings:preferences.timeZone.kathmandu') },
                { value: 'Asia/Kolkata', labelKey: tk('settings:preferences.timeZone.kolkata') },
                { value: 'UTC', labelKey: tk('settings:preferences.timeZone.utc') },
                { value: 'America/Los_Angeles', labelKey: tk('settings:preferences.timeZone.losAngeles') },
                { value: 'Europe/London', labelKey: tk('settings:preferences.timeZone.london') },
            ],
        }),
        field('field.preferences.defaultPageSize', 'defaultPageSize', {
            labelKey: tk('settings:preferences.defaultPageSize.label'), fieldType: 'number', layout: { md: 6 },
            validation: { rules: [
                    { ruleId: vri('integer'), params: { min: 5, max: 100 }, messageKey: tk('settings:preferences.defaultPageSize.error') },
                    { ruleId: vri('min'), params: { min: 5 }, messageKey: tk('settings:preferences.defaultPageSize.error') },
                    { ruleId: vri('max'), params: { max: 100 }, messageKey: tk('settings:preferences.defaultPageSize.error') },
                ] },
        }),
    ],
    actions: [
        action({ id: 'action.reset', labelKey: 'settings:preferences.resetDefaults', iconKey: 'refresh', kind: 'localUi', intent: 'secondary' }),
        action({ id: 'action.cancel', labelKey: 'common:cancel', iconKey: 'cancel', kind: 'localUi', intent: 'secondary', submitConfig: { validatesForm: false, requiresDirty: true } }),
        action({
            id: 'action.save',
            labelKey: 'settings:preferences.save',
            iconKey: 'save',
            commandId: 'preferences:update',
            operationId: PREFERENCES_UPDATE_OPERATION,
            submitConfig: { validatesForm: true, requiresDirty: true, resetBehavior: 'toSubmittedValues' },
        }),
    ],
    submission: {
        defaultCommandId: cdi('preferences:update'),
        successMessageKey: tk('settings:preferences.saved'),
        errorMessageKey: tk('settings:preferences.saveError'),
    },
};
export const ACCOUNT_PASSWORD_FORM = {
    id: fdi('form.settings.password'),
    schemaVersion: 1,
    module: SETTINGS_MODULE,
    feature: fk('password'),
    telemetryId: ti('form.settings.password'),
    accessibilityLabelKey: tk('settings:password.title'),
    privacyClassification: 'CONFIDENTIAL',
    availability: 'AVAILABLE',
    owner: 'contracts',
    operationId: OPERATION_IDS.AUTH_PASSWORD_CHANGE,
    titleKey: tk('settings:password.title'),
    mode: 'edit',
    fields: [
        field('field.password.currentPassword', 'currentPassword', {
            labelKey: tk('settings:password.currentPassword'), fieldType: 'password', layout: { md: 12 },
            validation: { rules: [{ ruleId: vri('required'), messageKey: tk('settings:password.currentRequired') }] },
        }),
        field('field.password.newPassword', 'newPassword', {
            labelKey: tk('settings:password.newPassword'), fieldType: 'password', layout: { md: 12 },
            validation: { rules: [
                    { ruleId: vri('required'), messageKey: tk('settings:password.newRequired') },
                    { ruleId: vri('crossField'), params: { field: 'currentPassword', operator: 'notEquals' }, messageKey: tk('settings:password.sameAsCurrent') },
                ] },
        }),
        field('field.password.confirmPassword', 'confirmPassword', {
            labelKey: tk('settings:password.confirmPassword'), fieldType: 'password', layout: { md: 12 },
            validation: { rules: [
                    { ruleId: vri('required'), messageKey: tk('settings:password.mismatch') },
                    { ruleId: vri('crossField'), params: { field: 'newPassword', operator: 'equals' }, messageKey: tk('settings:password.mismatch') },
                ] },
        }),
    ],
    actions: [action({
            id: 'action.save',
            labelKey: 'settings:password.submit',
            iconKey: 'save',
            commandId: 'password:change',
            operationId: OPERATION_IDS.AUTH_PASSWORD_CHANGE,
            submitConfig: { validatesForm: true, resetBehavior: 'toInitialValues' },
        })],
    submission: {
        defaultCommandId: cdi('password:change'),
        successMessageKey: tk('settings:password.success'),
        errorMessageKey: tk('settings:password.error'),
    },
};
export const ACCOUNT_SETTINGS_FORMS = {
    profile: ACCOUNT_PROFILE_FORM,
    preferences: ACCOUNT_PREFERENCES_FORM,
    password: ACCOUNT_PASSWORD_FORM,
};
export const ACCOUNT_SETTINGS_SECTION_ROUTE_IDS = {
    profile: [
        ROUTE_IDS.SETTINGS_PROFILE,
        ROUTE_IDS.SETTINGS_PREFERENCES,
    ],
    security: [
        ROUTE_IDS.SETTINGS_SECURITY,
        ROUTE_IDS.SETTINGS_PASSWORD,
        ROUTE_IDS.SETTINGS_MFA,
        ROUTE_IDS.SETTINGS_SESSIONS,
    ],
};
export function accountSettingsRouteForPath(pathname) {
    if (pathname === '/settings/profile')
        return ROUTE_IDS.SETTINGS_PROFILE;
    if (pathname === '/settings/security')
        return ROUTE_IDS.SETTINGS_SECURITY;
    return undefined;
}
